import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Download, X, RefreshCw, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ShareWeek({ activeId }) {
  const [open, setOpen] = useState(false);
  const [cover, setCover] = useState(null);
  const [loading, setLoading] = useState(false);

  const build = async (refresh = false) => {
    setLoading(true);
    if (refresh) setCover(null);
    try { const { data } = await api.get(`/plan/${activeId}/cover${refresh ? "?refresh=1" : ""}`); setCover(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setOpen(false); }
    finally { setLoading(false); }
  };

  const openSheet = () => { setOpen(true); if (!cover) build(); };
  const url = cover ? `${API_BASE}${cover.url}` : null;

  const share = async () => {
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], "our-week-sorted.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Our week is sorted", text: cover.share_text });
        return;
      }
      download(blob);
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open share — downloaded it instead"); }
  };

  const download = (blob) => {
    const a = document.createElement("a");
    a.href = blob ? URL.createObjectURL(blob) : url; a.download = "our-week-sorted.png"; a.click();
    toast.success("Saved — drop it in the family chat");
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(cover.share_text); toast.success("Week copied as text"); } catch { toast.error("Couldn't copy"); }
  };

  return (
    <>
      <button onClick={openSheet} data-testid="share-week-btn"
        className="mt-3 w-full bg-white border-2 border-[#FDECEF] rounded-2xl py-3 flex items-center justify-center gap-2 text-sorted-ink font-bold text-sm shadow-soft">
        <Share2 size={16} strokeWidth={2.5} /> Share the week with the fam
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => setOpen(false)} data-testid="share-week-sheet">
          <div className="w-full max-w-md bg-[#FFF6F1] rounded-t-[32px] p-5 pb-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-hand text-3xl text-sorted-ink">Photo cover page</h3>
              <button onClick={() => setOpen(false)} data-testid="share-week-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-ink"><X size={17} strokeWidth={2.5} /></button>
            </div>
            <p className="text-xs font-semibold text-sorted-slate mt-1">One card, seven dinners — perfect for the family group chat.</p>
            <div className="mt-4 rounded-3xl overflow-hidden border-2 border-white shadow-soft bg-white aspect-[4/5]">
              {loading || !cover ? (
                <div className="w-full h-full animate-pulse bg-[#FCE7EC] flex items-center justify-center text-sm font-bold text-sorted-slate" data-testid="share-week-loading">Millie's drawing it up…</div>
              ) : (
                <img src={url} alt="This week's dinners" className="w-full h-full object-cover" data-testid="share-week-image" />
              )}
            </div>
            {cover && cover.photos_ready < cover.total && (
              <p className="text-[11px] font-semibold text-sorted-slate mt-2">{cover.total - cover.photos_ready} dish photo{cover.total - cover.photos_ready === 1 ? "" : "s"} still cooking — hit refresh in a minute for the full set.</p>
            )}
            <div className="grid grid-cols-3 gap-2 mt-4">
              <button onClick={share} disabled={!cover} data-testid="share-week-share" className="col-span-3 bg-sorted-rose text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"><Share2 size={17} strokeWidth={2.5} /> Share</button>
              <button onClick={() => download()} disabled={!cover} data-testid="share-week-download" className="bg-white border-2 border-[#FDECEF] rounded-2xl py-3 flex flex-col items-center gap-1 text-sorted-ink text-[11px] font-bold disabled:opacity-50"><Download size={16} strokeWidth={2.5} />Save</button>
              <button onClick={copy} disabled={!cover} data-testid="share-week-copy" className="bg-white border-2 border-[#FDECEF] rounded-2xl py-3 flex flex-col items-center gap-1 text-sorted-ink text-[11px] font-bold disabled:opacity-50"><Copy size={16} strokeWidth={2.5} />Copy text</button>
              <button onClick={() => build(true)} disabled={loading} data-testid="share-week-refresh" className="bg-white border-2 border-[#FDECEF] rounded-2xl py-3 flex flex-col items-center gap-1 text-sorted-ink text-[11px] font-bold"><RefreshCw size={16} strokeWidth={2.5} className={loading ? "animate-spin" : ""} />Refresh</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
